import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import PropTypes from 'prop-types';

import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import {
  bankAccountApprovalActivatedStatusType,
  bankAccountApprovalFailedVerificationStatusType,
  bankAccountApprovalStatus,
  bankAccountPurposeTypes,
  bankAccountSetupTypes,
  bankAccountTypes,
} from '../../util/enum/api/bankAccountTypes';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import { uBold } from '../../assets/styles/utility';
import Badge from './Badge';
import { ReactComponent as DeleteIcon } from '../../assets/images/svg/delete-icon.svg';
import Paragraph from '../Paragraph/Paragraph';
import IconButtonWithLabel from './IconButtonWithLabel';
import Button from '../Button/Button';
import {
  getBankAccountStatus,
  shouldRenderRequirements,
  isBankAccountVerified,
  shouldRenderVoidedCheckUpload,
  getVoidedCheckStatus,
  completeStatus,
  verificationPendingStatus,
  incompleteStatus,
  isExpired,
} from '../../util/helpers/bankAccountHelper';
import { separateByUppercase } from '../../util/helpers/stringHelpers';
import AnchorOnClick from '../Anchor/AnchorOnClick';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import { ACC_MASK_PADDING } from '../../constants/bankAccounts';
import VerifyAccountModal from '../Modals/VerifyAccountModal';
import { ReactComponent as UnverifiedIcon } from '../../assets/images/svg/unverified_alt.svg';
import { ReactComponent as AlertTriangle } from '../../assets/images/svg/alert-triangle.svg';
import { ReactComponent as CheckRound } from '../../assets/images/svg/executed.svg';
import { ReactComponent as PendingClock } from '../../assets/images/svg/pending.svg';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import { isUserAccountApproved } from '../../constants/nagDashboardContants';
import { FUND_DEPOSIT_START } from '../../constants/gtmEvents';
import useGtmHook from '../../util/hooks/useGtmHook';
import { DEPOSIT_WIZARD_TYPES } from '../../constants/standardDepositWizardConstants';
import { useCrmEvent } from '../../util/helpers/crmEventsHelper';
import {
  EVENT_TYPES_ENUM,
  FUND_SELECTED_METHOD_TYPE,
} from '../../util/enum/api/crmEvents';

export const BankAccountCardWrapper = styled.li`
  padding: ${pxToRem(16)};
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: ${pxToRemMd(24)} ${pxToRemMd(16)};
  }
`;

export const BankAccountHardHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
`;

export const BankAccountCardHeaderContent = styled.div`
  ${uBold};
  margin-right: auto;
  padding-right: ${pxToRem(32)};
`;

export const BankAccountActionButtons = styled.div`
  display: flex;
  > button {
    min-width: ${pxToRem(120)};
  }
  > :first-child {
    margin-right: ${pxToRem(18)};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 100%;
    margin-top: ${pxToRemMd(16)};
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${pxToRemMd(20)};

    button {
      width: 100%;
      min-width: 0;
    }

    > :first-child {
      margin-right: 0;
    }
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    button {
      min-width: ${pxToRemMd(120)};
      font-size: ${pxToRemMd(16)};
    }

    > :first-child {
      margin-right: ${pxToRemMd(18)};
    }
  }
`;

export const BankAccountCardSubtext = styled.span`
  color: ${themeColors.colorTextSecondary};
  font-size: small;
  margin-bottom: ${pxToRemMd(4)};
`;

export const BankAccountStatusText = styled.span`
  color: ${themeColors.colorTextSecondary};
  padding: 0 ${pxToRem(16)};
  min-height: ${pxToRem(32)};
  min-width: ${pxToRem(120)};
  font-size: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-grow: 1;
    min-height: ${pxToRemMd(40)};
    padding: ${pxToRemMd(8)} ${pxToRemMd(16)};
    font-size: ${pxToRemMd(18)};
    line-height: 1.33;
  }
`;

export const BankAccountRequirementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${pxToRem(8)};
`;

export const BadgeWrapper = styled.div`
  margin-bottom: ${pxToRem(16)};
`;

export const ButtonBankAccountCard = styled(Button)`
  margin-top: ${pxToRem(8)};
  width: max-content;
`;

const TextWrapper = styled.span`
  display: flex;
  align-items: center;
`;

const Spacer = styled.span`
  display: inline;
  margin-left: ${pxToRem(4)};
  color: ${({ color = themeColors.colorTextPrimary }) => color};
`;

const BadgeAndTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${pxToRem(8)};
`;

const BadgeAndIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Alert = styled(AlertTriangle)`
  height: ${pxToRem(16)};
  color: red;
`;

const Unverified = styled(UnverifiedIcon)`
  height: ${pxToRem(16)};
  color: ${themeColors.colorTextPrimary};
`;

const PendingIcon = styled(PendingClock)`
  height: ${pxToRem(16)};
  color: ${themeColors.colorWarning};
`;

const Check = styled(CheckRound)`
  height: ${pxToRem(16)};
  color: ${themeColors.colorSuccess};
`;

const BankAccountCard = ({
  bankAccount,
  handleVerifyAccount,
  handleDeposit,
  handleRemoveAccount,
  allowAccountChangesPermission,
}) => {
  const { t } = useTranslation();
  const { displayBasicAccountOptionsPermission } = useCheckPermissions();
  const account = useSelector(getCurrentAccount);
  const type = separateByUppercase(
    parseEnumType(bankAccountTypes, bankAccount?.BankAccountType),
  );
  const { fundGtmEvent } = useGtmHook();
  const handleCrmEvent = useCrmEvent();
  const [isOpenAccountVerifyModal, setIsOpenAccountverifyModal] = useState(
    false,
  );

  if (!bankAccount || isEmpty(bankAccount)) {
    return null;
  }

  const bankAccountStatus = getBankAccountStatus(bankAccount);
  const voidedCheckStatus = getVoidedCheckStatus(bankAccount).status;

  const isWireBankAccount =
    parseEnumType(bankAccountPurposeTypes, bankAccount?.PurposeType) ===
    parseEnumType(bankAccountPurposeTypes, 2);

  const shouldRenderMicroDepositButton = () => {
    const { SetupType, StateType } = bankAccount;
    const stateType = parseEnumType(bankAccountApprovalStatus, StateType);
    if (
      parseEnumType(bankAccountSetupTypes, SetupType) ===
        parseEnumType(bankAccountSetupTypes, 3) ||
      (parseEnumType(bankAccountSetupTypes, SetupType) ===
        parseEnumType(bankAccountSetupTypes, 2) &&
        stateType === bankAccountApprovalActivatedStatusType) ||
      stateType === bankAccountApprovalFailedVerificationStatusType
    ) {
      return false;
    }

    return true;
  };

  // const isManualACHAccount = () => {
  //   const { SetupType } = bankAccount;

  //   if (
  //     parseEnumType(bankAccountSetupTypes, SetupType) ===
  //     parseEnumType(bankAccountSetupTypes, 2)
  //   ) {
  //     return true;
  //   }

  //   return false;
  // };

  const shouldRenderBankAccountBadge = () => {
    const verifiedStatus = t('bankAccounts.verificationStatus.verified');
    const isCompleteState =
      parseEnumType(bankAccountApprovalStatus, bankAccount?.StateType) ===
      parseEnumType(bankAccountApprovalStatus, 5);
    if (isWireBankAccount) {
      if (isCompleteState) {
        return false;
      }

      return true;
    }

    const { status } = bankAccountStatus;

    if (
      status === verifiedStatus ||
      isCompleteState ||
      status === verificationPendingStatus
    ) {
      return false;
    }

    return true;
  };

  const shouldShowBankAccountDepositButton = (bankAccount) => {
    if (isWireBankAccount) {
      return false;
    }

    return isBankAccountVerified(bankAccount);
  };

  const openVerifyAccountModal = () => {
    setIsOpenAccountverifyModal(true);
  };

  const handleFundingGtmEvent = () => {
    fundGtmEvent(FUND_DEPOSIT_START, {
      fund: {
        method: DEPOSIT_WIZARD_TYPES.ACH,
      },
    });
  };

  return (
    <BankAccountCardWrapper key={bankAccount.BankAccountUid}>
      <BankAccountHardHeader>
        <BankAccountCardHeaderContent>
          <Paragraph fontSize={18} bold isFlex>
            {`${bankAccount.BankName}, ${ACC_MASK_PADDING()}${
              bankAccount.AccountMask
            }`}{' '}
            ({type})
          </Paragraph>
        </BankAccountCardHeaderContent>

        {handleRemoveAccount && (
          <IconButtonWithLabel
            type="button"
            color={themeColors.colorIconPrimary}
            onClick={() =>
              handleRemoveAccount(
                bankAccount?.BankAccountUid,
                parseEnumType(
                  bankAccountPurposeTypes,
                  bankAccount?.PurposeType,
                ),
                bankAccount?.BankAccountType,
              )
            }
            disabled={
              !allowAccountChangesPermission || !isUserAccountApproved(account)
            }
          >
            <DeleteIcon width={12} height={12} />
            <Paragraph fontSize={16} marginLeft={6} textDecoration="underline">
              {t('common.delete')}
            </Paragraph>
          </IconButtonWithLabel>
        )}
      </BankAccountHardHeader>
      {!isWireBankAccount && (
        <BankAccountActionButtons>
          <ButtonBankAccountCard
            variant="primary"
            type="button"
            disabled={
              !allowAccountChangesPermission ||
              displayBasicAccountOptionsPermission ||
              !shouldShowBankAccountDepositButton(bankAccount) ||
              !isUserAccountApproved(account)
            }
            onClick={() => {
              handleDeposit(bankAccount);
              handleFundingGtmEvent();
              handleCrmEvent(
                account,
                EVENT_TYPES_ENUM.Fund_SelectedMethodType,
                {
                  MethodType: FUND_SELECTED_METHOD_TYPE.ACH,
                },
              );
            }}
          >
            {i18next.t('bankAccounts.depositBtn')}
          </ButtonBankAccountCard>
        </BankAccountActionButtons>
      )}
      <BadgeAndTextWrapper>
        {shouldRenderBankAccountBadge() && (
          <BadgeAndIconWrapper>
            {!isWireBankAccount && <Alert height={pxToRem(16)} />}
            {isWireBankAccount && voidedCheckStatus === incompleteStatus && (
              <PendingIcon height={pxToRem(16)} />
            )}
            <Badge {...bankAccountStatus} />
          </BadgeAndIconWrapper>
        )}
      </BadgeAndTextWrapper>
      {shouldRenderRequirements(bankAccount) &&
        shouldRenderMicroDepositButton() && (
          <BankAccountRequirementWrapper>
            <Paragraph marginBottom={pxToRem(4)} marginTop={pxToRem(4)}>
              <AnchorOnClick
                onClick={() => {
                  handleVerifyAccount(bankAccount?.BankAccountUid);
                }}
              >
                {t('bankAccounts.confirmMicroDepositLinkText')}
              </AnchorOnClick>
              <Spacer />
              {t('bankAccounts.microDepositText')}
            </Paragraph>
          </BankAccountRequirementWrapper>
        )}
      {shouldRenderVoidedCheckUpload(bankAccount) && (
        <BankAccountRequirementWrapper>
          <Paragraph marginBottom={pxToRem(4)} marginTop={pxToRem(4)}>
            <TextWrapper>
              <Unverified />
              <Spacer>
                {t('bankAccounts.notVerified')}
                <Spacer />
                <AnchorOnClick onClick={openVerifyAccountModal}>
                  {t('common.learnMore')}
                </AnchorOnClick>
              </Spacer>
            </TextWrapper>
          </Paragraph>
        </BankAccountRequirementWrapper>
      )}
      {!isExpired(bankAccount) &&
        voidedCheckStatus === verificationPendingStatus && (
          <BadgeAndTextWrapper>
            <BadgeAndIconWrapper>
              <PendingIcon height={pxToRem(16)} />
              <Badge {...bankAccountStatus} />
            </BadgeAndIconWrapper>
            {!isWireBankAccount && (
              <Paragraph color={themeColors.colorTextSecondary} marginTop={12}>
                {t('bankAccounts.underReview')}
              </Paragraph>
            )}
          </BadgeAndTextWrapper>
        )}
      {!isExpired(bankAccount) &&
        !isWireBankAccount &&
        voidedCheckStatus === completeStatus && (
          <BadgeAndTextWrapper>
            <BadgeAndIconWrapper>
              {!shouldRenderRequirements(bankAccount) && (
                <Check height={pxToRem(16)} />
              )}
              <Badge {...bankAccountStatus} />
            </BadgeAndIconWrapper>
          </BadgeAndTextWrapper>
        )}
      {isOpenAccountVerifyModal && (
        <VerifyAccountModal
          handleOutsideClick
          isOpen={isOpenAccountVerifyModal}
          close={setIsOpenAccountverifyModal}
          bankAccount={bankAccount}
        />
      )}
    </BankAccountCardWrapper>
  );
};

export default BankAccountCard;
BankAccountCard.defaultProps = {
  isDepositButtonVisible: true,
};
BankAccountCard.propTypes = {
  bankAccount: PropTypes.shape({
    BankAccountUid: PropTypes.string,
    StateType: PropTypes.number,
    PurposeType: PropTypes.number,
    BankAccountType: PropTypes.number,
    BankName: PropTypes.string,
    AccountMask: PropTypes.string,
    IsVerified: PropTypes.bool,
    VoidedCheckStatusType: PropTypes.number,
    MicroDepositStatusType: PropTypes.number,
    EncriptedBrokerAccountUid: PropTypes.string,
    EncriptedBankAccountUid: PropTypes.string,
    SetupType: PropTypes.number,
  }),
  handleVerifyAccount: PropTypes.func,
  handleErrorAccount: PropTypes.func,
  isACHAccount: PropTypes.bool,
  handleDeposit: PropTypes.func,
  handleRemoveAccount: PropTypes.func,
  allowAccountChangesPermission: PropTypes.bool,
  contactSupport: PropTypes.func,
  isDepositButtonVisible: PropTypes.bool,
};
